import React from "react";
import styled from "styled-components";
import { X } from "react-feather";
import Flatpickr from "react-flatpickr";
import PropTypes from "prop-types";
import CustomSelect from "../dropdown";

import "flatpickr/dist/themes/material_blue.css";

const customStyles = {
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "none",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    display: "none",
  }),
};

const StyledInputGroup = styled.div`
  .form-control {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;

    &:focus {
      box-shadow: rgba(33, 35, 38, 0.1) 0px 10px 10px -10px;
    }

    &:has(.select__control--is-focused) {
      border-color: #86b7fe;
    }
  }

  &:has(.form-control:focus) {
    button {
      box-shadow: rgba(33, 35, 38, 0.1) 0px 10px 10px -10px;
      border-color: #86b7fe;
    }
  }

  &:has(.select__control--is-focused) {
    button {
      border-color: #86b7fe;
    }
  }

  button {
    border-bottom: 1px solid #ced4da;
    border-radius: 0;
    border-top: none;
    border-right: none;
    border-left: none;
  }

  .mobile-input-wrapper {
    position: relative;
    width: 100%;

    .mobile-prefix {
      position: absolute;
      top: 50%;
      left: 12px;
      transform: translateY(-50%);
      color: #6c757d; // gray text
      font-size: 14px;
      pointer-events: none;
    }

    .mobile-input {
      padding-left: 45px !important; // give space for the prefix
    }
  }
`;

const InputGroup = ({
  value,
  placeholder,
  handleOnChange,
  handleClearInput,
  minDate,
  maxDate,
  type,
  options = [],
  maxLength,
}) => {
  let inputComponent = null;

  switch (type) {
    case "text":
      inputComponent = (
        <input
          type="text"
          maxLength={maxLength || null}
          className="form-control"
          placeholder={placeholder || ""}
          value={value || ""}
          onChange={handleOnChange}
        />
      );
      break;

    case "date":
      inputComponent = (
        <Flatpickr
          value={value || new Date()}
          placeholder="Date"
          className="form-control"
          options={{
            enableTime: true,
            dateFormat: "M d, Y",
            minDate: minDate || null,
            maxDate: maxDate || null,
          }}
          onChange={handleOnChange}
        />
      );
      break;

    case "select":
      inputComponent = (
        <CustomSelect
          className="form-control p-0"
          placeholder={placeholder}
          onChange={handleOnChange}
          value={value}
          options={options}
          styles={customStyles}
        />
      );
      break;

    case "mobile":
      inputComponent = (
        <input
          type="text"
          maxLength={11}
          minLength={11}
          autoComplete="off"
          className="form-control"
          // eslint-disable-next-line no-nested-ternary
          value={value ? (value.startsWith("0") ? value : `0${value}`) : ""}
          onKeyDown={(event) => {
            const allowedKeys = [
              "Backspace",
              "Delete",
              "ArrowLeft",
              "ArrowRight",
              "Tab",
            ];

            const isCtrlOrCmd = event.ctrlKey || event.metaKey;

            const isShortcut =
              isCtrlOrCmd &&
              ["a", "c", "v", "x"].includes(event.key.toLowerCase());

            if (
              !/[0-9]/.test(event.key) &&
              !allowedKeys.includes(event.key) &&
              !isShortcut
            ) {
              event.preventDefault();
            }
          }}
          onPaste={(evt) => {
            evt.preventDefault();
            const pastedText = evt.clipboardData.getData("text");
            const cleanNumber = pastedText.replace(/\D/g, "");

            // Check if pasted number is valid format (starts with 09 or 9)
            if (cleanNumber.startsWith("09") || cleanNumber.startsWith("9")) {
              const numberToSave = cleanNumber.startsWith("0")
                ? cleanNumber.substring(1)
                : cleanNumber;
              handleOnChange(numberToSave);
            }
          }}
          onChange={(evt) => {
            const val = evt.target.value.replace(/\D/g, "");

            // Handle empty value
            if (val === "") {
              handleOnChange("");

              return;
            }

            // Allow progressive typing
            if (val === "0") {
              handleOnChange("0");
            } else if (val.startsWith("09")) {
              // Valid pattern "09xxxxxxx", remove leading zero for storage
              handleOnChange(val.substring(1));
            } else if (val.startsWith("9")) {
              // User typed "9" directly, store as is
              handleOnChange(val);
            }
            // Other patterns are ignored (not stored)
          }}
        />
      );
      break;
    default:
      break;
  }

  return (
    <StyledInputGroup className="input-group">
      <div className="input-group-append d-flex w-100">
        {inputComponent}
        <button
          className="btn clear-button"
          type="button"
          onClick={handleClearInput}
        >
          <X />
        </button>
      </div>
    </StyledInputGroup>
  );
};

InputGroup.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  handleOnChange: PropTypes.func,
  handleClearInput: PropTypes.func,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  type: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  maxLength: PropTypes.number,
};

export default InputGroup;
